import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  ChevronLeft,
  ChevronRight,
  FileText,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { TradingAccountModal } from '@/components/trading/accounting/account-modal';
import { ChartDeleteOptions } from '@/components/accounting/chart-delete-options';

// TDMS Account Type Labels
const TDMS_TYPE_LABELS: Record<string, string> = {
  CURRENT_ASSET: 'Donen Varliklar',
  FIXED_ASSET: 'Duran Varliklar',
  SHORT_TERM_LIABILITY: 'Kisa Vadeli Yab. Kaynaklar',
  LONG_TERM_LIABILITY: 'Uzun Vadeli Yab. Kaynaklar',
  EQUITY: 'Oz Kaynaklar',
  INCOME: 'Gelirler',
  COST: 'Giderler/Maliyetler',
  FREE: 'Serbest',
  OFF_BALANCE: 'Nazim Hesaplar',
};

// TDMS Balance Type Labels
const BALANCE_TYPE_LABELS: Record<string, string> = {
  B: 'Borc (Debit)',
  A: 'Alacak (Credit)',
  C: 'Cift (Both)',
};

export function TradingAccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('tradingAccounting');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [cascadeDelete, setCascadeDelete] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);

  const { data: account, isLoading } = trpc.tradingAccount.byId.useQuery(id!, {
    enabled: !!id,
  });

  const { data: ledgerData, isLoading: isLedgerLoading } = trpc.tradingAccount.getLedger.useQuery(
    {
      accountId: id!,
      page: ledgerPage,
    },
    { enabled: !!id }
  );

  const utils = trpc.useUtils();

  const updateMutation = trpc.tradingAccount.update.useMutation({
    onSuccess: () => {
      utils.tradingAccount.byId.invalidate(id);
      utils.tradingAccount.list.invalidate();
    },
  });

  // Prefer history-based back navigation so the user lands on the exact
  // tab + scroll position they came from. Falls back to the accounts tab
  // when there is no history (e.g. opening the detail via a direct URL).
  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/trading/accounting?tab=accounts');
    }
  };

  // Preparation mode adds cascade + merge to the delete dialog and lets the
  // dialog be opened for accounts the locked chart would never delete.
  const { data: chartPrep } = trpc.tradingAccount.chartPrepStatus.useQuery();
  const unlocked = chartPrep?.enabled === true;
  const { data: mergeCandidates } = trpc.tradingAccount.list.useQuery(
    { limit: 500 },
    { enabled: unlocked && isDeleteDialogOpen }
  );

  const deleteMutation = trpc.tradingAccount.delete.useMutation({
    onSuccess: () => {
      goBack();
    },
  });

  const handleToggleActive = () => {
    if (account) {
      updateMutation.mutate({
        id: account.id,
        isActive: !account.isActive,
      });
    }
  };

  const handleDelete = () => {
    if (!account) return;
    // Preparation mode carries the cascade / merge choices; the locked chart
    // still sends the bare id it always sent.
    deleteMutation.mutate(
      unlocked
        ? {
            id: account.id,
            cascade: cascadeDelete,
            ...(mergeTargetId ? { reassignToAccountId: mergeTargetId } : {}),
          }
        : account.id
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="flex h-96 flex-col items-center justify-center">
        <p className="text-lg font-medium text-muted-foreground">{t('accountDetail.notFound', 'Account not found')}</p>
        <Button variant="link" onClick={goBack}>
          {t('accountDetail.backToAccounting', 'Back to Accounting')}
        </Button>
      </div>
    );
  }

  const typeColors: Record<string, string> = {
    CURRENT_ASSET: 'bg-blue-100 text-blue-700',
    FIXED_ASSET: 'bg-blue-200 text-blue-800',
    SHORT_TERM_LIABILITY: 'bg-red-100 text-red-700',
    LONG_TERM_LIABILITY: 'bg-red-200 text-red-800',
    EQUITY: 'bg-purple-100 text-purple-700',
    INCOME: 'bg-green-100 text-green-700',
    COST: 'bg-orange-100 text-orange-700',
    FREE: 'bg-gray-100 text-gray-700',
    OFF_BALANCE: 'bg-gray-200 text-gray-800',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={goBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{account.code}</h1>
              <span className={`px-2 py-0.5 text-xs font-medium ${typeColors[account.type]}`}>
                {TDMS_TYPE_LABELS[account.type] || account.type}
              </span>
              <span className="bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {account.balanceType} - {BALANCE_TYPE_LABELS[account.balanceType]?.split(' ')[0]}
              </span>
              {!account.isActive && (
                <span className="bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                  {t('accountDetail.inactive', 'Inactive')}
                </span>
              )}
              {account.isSystemAccount && (
                <span className="bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
                  {t('accountDetail.system', 'System')}
                </span>
              )}
            </div>
            <p className="text-lg text-muted-foreground">{account.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-8" onClick={handleToggleActive} disabled={updateMutation.isPending}>
            {account.isActive ? (
              <>
                <ToggleRight className="mr-2 h-4 w-4" />
                {t('accountDetail.deactivate', 'Deactivate')}
              </>
            ) : (
              <>
                <ToggleLeft className="mr-2 h-4 w-4" />
                {t('accountDetail.activate', 'Activate')}
              </>
            )}
          </Button>
          <Button variant="outline" className="h-8" onClick={() => setIsEditModalOpen(true)}>
            <Edit className="mr-2 h-4 w-4" />
            {t('accountDetail.edit', 'Edit')}
          </Button>
          {(unlocked || (!account.isSystemAccount && account._count.journalLines === 0)) && (
            <Button variant="destructive" className="h-8" onClick={() => setIsDeleteDialogOpen(true)}>
              <Trash2 className="mr-2 h-4 w-4" />
              {t('accountDetail.delete', 'Delete')}
            </Button>
          )}
        </div>
      </div>

      {/* Account Details */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Info Card */}
        <div className="border border-border bg-card p-6">
          <h3 className="text-lg font-semibold mb-4">{t('accountDetail.details', 'Account Details')}</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('accountDetail.code', 'Code')}</dt>
              <dd className="font-mono font-medium">{account.code}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('accountDetail.type', 'Type')}</dt>
              <dd>{TDMS_TYPE_LABELS[account.type] || account.type}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('accountDetail.balanceType', 'Balance Type')}</dt>
              <dd>{account.balanceType} - {BALANCE_TYPE_LABELS[account.balanceType]}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('accountDetail.currency', 'Currency')}</dt>
              <dd>{account.currency}</dd>
            </div>
            {account.parent && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{t('accountDetail.parent', 'Parent')}</dt>
                <dd>
                  <Link
                    to={`/trading/accounting/accounts/${account.parent.id}`}
                    className="text-primary hover:underline"
                  >
                    {account.parent.code} - {account.parent.name}
                  </Link>
                </dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{t('accountDetail.journalEntries', 'Journal Entries')}</dt>
              <dd>{account._count.journalLines}</dd>
            </div>
          </dl>
          {account.description && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">{account.description}</p>
            </div>
          )}
        </div>

        {/* Balance Card */}
        <div className="border border-border bg-card p-6">
          <h3 className="text-lg font-semibold mb-4">{t('accountDetail.currentBalance', 'Current Balance')}</h3>
          <div className="flex items-center gap-3">
            <div className={`p-3 ${account.balance >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              {account.balance >= 0 ? (
                <TrendingUp className={`h-6 w-6 ${account.balance >= 0 ? 'text-green-600' : 'text-red-600'}`} />
              ) : (
                <TrendingDown className="h-6 w-6 text-red-600" />
              )}
            </div>
            <div>
              <p className={`text-3xl font-bold ${account.balance < 0 ? 'text-red-600' : ''}`}>
                {formatCurrency(account.balance, account.currency)}
              </p>
              <p className="text-sm text-muted-foreground">
                {BALANCE_TYPE_LABELS[account.balanceType]} {t('accountDetail.balanceAccount', 'balance account')}
              </p>
            </div>
          </div>
        </div>

        {/* Child Accounts */}
        {account.children.length > 0 && (
          <div className="border border-border bg-card p-6">
            <h3 className="text-lg font-semibold mb-4">
              {t('accountDetail.childAccounts', 'Child Accounts')} ({account.children.length})
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {account.children.map((child) => (
                <Link
                  key={child.id}
                  to={`/trading/accounting/accounts/${child.id}`}
                  className="flex justify-between items-center p-2 hover:bg-muted/50 transition-colors"
                >
                  <div>
                    <span className="font-mono text-sm">{child.code}</span>
                    <span className="ml-2 text-sm">{child.name}</span>
                    {!child.isActive && (
                      <span className="ml-2 text-xs text-muted-foreground">({t('accountDetail.inactive', 'Inactive')})</span>
                    )}
                  </div>
                  <span className={`font-mono text-sm ${child.balance < 0 ? 'text-red-600' : ''}`}>
                    {formatCurrency(child.balance, account.currency)}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Ledger */}
      <div className="border border-border bg-card">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-lg font-semibold">{t('accountDetail.accountLedger', 'Account Ledger')}</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              {ledgerData?.pagination.total || 0} {t('accountDetail.transactions', 'transactions')}
            </p>
          </div>
        </div>

        {isLedgerLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : ledgerData?.entries.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-medium">{t('accountDetail.noTransactions', 'No transactions yet')}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('accountDetail.noTransactionsHelp', 'This account has no posted journal entries.')}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t('accountDetail.date', 'Date')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t('accountDetail.entryNumber', 'Entry #')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t('accountDetail.description', 'Description')}</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">{t('accountDetail.debit', 'Debit')}</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">{t('accountDetail.credit', 'Credit')}</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">{t('accountDetail.balance', 'Balance')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {ledgerData?.entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm">
                        {new Date(entry.journalEntry.date).toLocaleDateString(dateLocale)}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/trading/accounting/journal-entries/${entry.journalEntry.id}`}
                          className="font-mono text-sm text-primary hover:underline"
                        >
                          {entry.journalEntry.entryNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm">{entry.description || entry.journalEntry.description}</p>
                        {(entry.tradingCustomer || entry.supplier) && (
                          <p className="text-xs text-muted-foreground">
                            {entry.tradingCustomer
                              ? entry.tradingCustomer.companyName
                              : entry.supplier?.companyName}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        {entry.debit > 0 ? formatCurrency(entry.debit, account.currency) : '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        {entry.credit > 0 ? formatCurrency(entry.credit, account.currency) : '-'}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono text-sm font-medium ${entry.runningBalance < 0 ? 'text-red-600' : ''}`}>
                        {formatCurrency(entry.runningBalance, account.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {ledgerData && ledgerData.pagination.totalPages > 1 && (
              <div className="flex justify-center gap-2 p-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setLedgerPage((p) => Math.max(1, p - 1))}
                  disabled={ledgerPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('pagination.previous', 'Previous')}
                </Button>
                <span className="px-4 py-2 text-sm">
                  {t('pagination.page', 'Page {{current}} of {{total}}', { current: ledgerPage, total: ledgerData.pagination.totalPages })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setLedgerPage((p) => Math.min(ledgerData.pagination.totalPages, p + 1))}
                  disabled={ledgerPage >= ledgerData.pagination.totalPages}
                >
                  {t('pagination.next', 'Next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      <TradingAccountModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        account={account}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('accountDetail.deleteTitle', 'Delete Account')}</DialogTitle>
            <DialogDescription>
              {t('accountDetail.deleteConfirmation', 'Are you sure you want to delete account "{{code}} - {{name}}"? This action cannot be undone.', { code: account.code, name: account.name })}
            </DialogDescription>
          </DialogHeader>
          <ChartDeleteOptions
            unlocked={unlocked}
            hasChildren={account.children.length > 0}
            hasPostings={account._count.journalLines > 0}
            cascade={cascadeDelete}
            onCascadeChange={setCascadeDelete}
            mergeTargetId={mergeTargetId}
            onMergeTargetChange={setMergeTargetId}
            options={(mergeCandidates?.accounts ?? [])
              // Leaves only: balances and postings never sit on a header account.
              .filter((candidate) => candidate.id !== account.id && candidate._count.children === 0)
              .map((candidate) => ({
                id: candidate.id,
                code: candidate.code,
                name: candidate.name,
              }))}
          />
          {deleteMutation.error && (
            <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {deleteMutation.error.message}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" className="h-8" onClick={() => setIsDeleteDialogOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              className="h-8"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('accountDetail.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
